import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { useUser } from "~/hooks/user";
import { useConfig } from "~/hooks/webconfig";
import {
  type HubSpotEnv,
  startHubSpotChat,
  suppressHubSpot,
} from "~/utils/hubspot-lifecycle";

interface HubSpotVisitorTokenData {
  hubspotVisitorToken: {
    token: string;
  };
}

const HUBSPOT_VISITOR_TOKEN_MUTATION = gql`
  mutation HubspotVisitorToken {
    hubspotVisitorToken {
      token
    }
  }
` as import(
  // eslint-disable-next-line lingui/no-unlocalized-strings -- package name
  "@graphql-typed-document-node/core"
).TypedDocumentNode<HubSpotVisitorTokenData>;

const HubSpotContext = createContext<{ open: () => void }>({
  open: () => {},
});

export const useHubSpot = () => useContext(HubSpotContext);

function browserEnv(): HubSpotEnv {
  return { window, document, MutationObserver };
}

export default function HubSpotInitializer({
  children,
  disabled = false,
}: React.PropsWithChildren<{ disabled?: boolean }>) {
  const config = useConfig();
  const user = useUser();
  const isAuthenticated = user?.authenticated;
  const email = user?.email;
  const pendingOpenRef = useRef(false);
  const [getVisitorToken] = useMutation(HUBSPOT_VISITOR_TOKEN_MUTATION);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!disabled) return;

    pendingOpenRef.current = false;
    // The returned restore runs when the route stops being suppressed.
    return suppressHubSpot(browserEnv());
  }, [disabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (disabled) return;
    if (!config.hubspotPortalId) return;
    if (document.getElementById("hs-script-loader")) return;

    window.hsConversationsSettings = {
      loadImmediately: !isAuthenticated,
    };

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "hs-script-loader";
    script.async = true;
    script.defer = true;
    // eslint-disable-next-line lingui/no-unlocalized-strings -- script src URL
    script.src = `//js-eu1.hs-scripts.com/${config.hubspotPortalId}.js`;
    document.body.appendChild(script);
  }, [config.hubspotPortalId, disabled, isAuthenticated]);

  useEffect(() => {
    if (typeof window === "undefined" || !config.hubspotPortalId) return;
    if (disabled) return;

    return startHubSpotChat(browserEnv(), {
      isAuthenticated: !!isAuthenticated,
      email,
      getVisitorToken: async () =>
        (await getVisitorToken()).data?.hubspotVisitorToken?.token,
      takePendingOpen: () => {
        const pending = pendingOpenRef.current;
        pendingOpenRef.current = false;
        return pending;
      },
    });
  }, [
    isAuthenticated,
    email,
    config.hubspotPortalId,
    disabled,
    getVisitorToken,
  ]);

  const open = useCallback(() => {
    if (disabled) return;
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.open();
    } else {
      pendingOpenRef.current = true;
    }
  }, [disabled]);

  return (
    <HubSpotContext.Provider value={{ open }}>
      {children}
    </HubSpotContext.Provider>
  );
}
